import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, ref } from 'vue';
import { useMounted, useNow } from '@vueuse/core';
import { MonetizationType, ObjectType, OfferType, PresentationType } from '@/@types/graphql-types';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';

import {
	BuyboxParam,
	DVD_BLURAY_MONETIZATION_TYPE_TRANSLATION_KEY,
	MONETIZATION_TYPE_TRANSLATION_KEY,
	OfferLocation,
} from '@/helpers/offer-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { shouldClickMask, clickMaskUrl } from '@/components/click-masking/click-masking-config';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';

import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { diffInDays, formatDateTime } from '@/helpers/date-helper';
import { openJwTvAvodModal } from '@/features/jwtv-avod/helpers/jwtv-avod-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { SnowplowPackageContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { SponsoredClickoutsHelper } from '@/features/sponsored-recommendations/helpers/sponsored-clickouts-helper';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIconWide from '@/features/ui/components/ProviderIconWide.vue';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { getClickoutTrackingPlacement, OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';
import { useProviderPromotion } from '@/helpers/provider-promotion';

import {
	useInternationalOffers,
	useInternationalOffersStatus,
} from '@/features/international-offers/composables/useInternationalOffers';

import { faClock } from '@fortawesome/free-solid-svg-icons';
import DisclaimerButton from '@/features/buybox/components/DisclaimerButton.vue';
import { getDisclaimerText } from '@/features/buybox/helpers/list-buybox-config';

import { useExperiment } from '@/libraries/experiment';
import { BuyboxRegularOffer } from '@/features/buybox/types/types';
import { sortAndDedupeOfferEpisodes } from '@/components/experiments/LinearTVExp';

import ListOfferCTA from '@/features/buybox/components/list/ListOfferCTA.vue';
import ListOfferPresentation from '@/features/buybox/components/list/ListOfferPresentation.vue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useAppStore } from '@/stores/pinia/useAppStore';

import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { useBuyRentExp } from '@/components/experiments/ExpBuyRent';
import { useSofterFreeTrialV2Exp } from '@/components/experiments/SoftFreeTrialListV2';
import { usePrimePlayPromoExp } from '@/components/experiments/PrimePlayPromoExp';
// EXP_PROVIDER_BUYBOX_V2
import { useProviderBuyboxExpTags } from '@/components/experiments/ProviderBuyboxExp/composables/useProviderBuyboxExpTags';
// EXP_PROVIDER_BUYBOX_V2
import { useGridMoreInfoExp } from '@/components/experiments/GridBuyboxRedesignExps/GridMoreInfoExp';
// EXP_PROMOTED_FREE_TRIAL_REDESIGN
import { usePromotedFreeTrialRedesignExp } from '@/components/experiments/PromotedFreeTrialRedesignExp';
// EXP_PROMOTED_FREE_TRIAL_REDESIGN
// EXP_LINEAR_TV
import { useLinearTVExp } from '@/components/experiments/LinearTVExp';
// EXP_LINEAR_TV
import NativeAdListBuyboxOffer from '@/features/native-ads/components/NativeAdListBuyboxOffer.vue';
import { useNativeAds } from '@/features/native-ads/composables/useNativeAds';
// EXP_LINEAR_TV
import LinearOfferCTA from '@/components/experiments/LinearTVExp/LinearOfferCTA.vue';
import LinearExtraShowings from '@/components/experiments/LinearTVExp/LinearExtraShowings.vue';
// EXP_LINEAR_TV


export default /*#__PURE__*/_defineComponent({
  __name: 'ListOffer',
  props: {
    offer: null,
    title: null,
    isPromotedBuyBox: { type: Boolean },
    isBundle: { type: Boolean },
    isEpisode: { type: Boolean },
    seasonNumber: null,
    episodeNumber: null,
    additionalContexts: null,
    rowIndex: null
  },
  emits: ["clickOut"],
  setup(__props: any) {

const props = __props as {
	offer: BuyboxRegularOffer;
	title: TitleDetail;
	isPromotedBuyBox?: boolean;
	isBundle?: boolean;
	isEpisode?: boolean;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	additionalContexts: SnowplowContext[];
	rowIndex?: number;
};

const BuyBoxInfoModal = () => import('@/features/buybox/components/list/BuyBoxInfoModal.vue');
const BundleIcons = () => import('@/features/buybox/components/list/BundleIcons.vue');
const ListOfferMBRExp = () => import('@/components/experiments/ExpBuyRent/components/ListOfferMBRExp.vue');



const { country, language, webLocale } = storeToRefs(useLanguageStore());

const { t, tc } = useI18n();
const { xServeTrack } = storeToRefs(useAppStore());

const isMounted = useMounted();

const buyboxOffer = computed(() => props.offer);

/* Native Ads
 * Renders a GAM replica of the Ad absolutely positioned on top of existing ad
 */
const { nativeAdsShortName } = useNativeAds({ title: () => props.title });
const isNativeAdCandidate = computed(
	() => !!nativeAdsShortName.value && props.offer.package.shortName === nativeAdsShortName.value && !props.isBundle
);

const { setGridMoreInfoTags } = useGridMoreInfoExp();
// EXP_PROVIDER_BUYBOX_V2
const { setProviderBuyboxExpTag } = useProviderBuyboxExpTags();
// EXP_PROVIDER_BUYBOX_V2
const { setBuyRentExpTags, trackingEventLabel } = useBuyRentExp({ title: () => props.title, offer: () => props.offer });
const { setSofterFreeTrialV2Tags } = useSofterFreeTrialV2Exp();
// EXP_PROMOTED_FREE_TRIAL_REDESIGN
const { setPromotedFreeTrialRedesignTags } = usePromotedFreeTrialRedesignExp(() => buyboxOffer.value.package.shortName);
// EXP_LINEAR_TV
const { setLinearTVExpTags } = useLinearTVExp();
// EXP_LINEAR_TV
// EXP_PROMOTED_FREE_TRIAL_REDESIGN

const isDvdOrBluRay = computed(() =>
	[PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(
		buyboxOffer.value?.presentationType
	)
);

const { activeVariantWithControlGroup: expMergeBuyRentVariant, trackEvent: trackBuyRentExpEvent } =
	useExperiment('EXP_MERGE_BUY_RENT');

const isBundleWithIconWide = computed(() => props.isBundle && buyboxOffer.value.bundleShortName === 'pap');

const hardcodedPackage = computed(() => {
	if (isBundleWithIconWide.value) {
		return {
			...buyboxOffer.value.package,
			iconWide: 'https://www.justwatch.com/appassets/img/bundles/applepeacock.{format}',
		};
	}

	return buyboxOffer.value.package;
});

const displayBuyBoxInfoModal = ref<boolean>(false);
const offerContainer = ref<HTMLElement | null>(null);

const isModalProvider = computed(() => ['jwt', 'otp', 'pxp', 'brp'].includes(buyboxOffer.value.package.shortName));

const _clickoutType = computed(() => {
	if (isModalProvider.value) return 'video_player';
	return props.isPromotedBuyBox ? 'promoted' : 'regular';
});

const { clickoutType, placement } = getClickoutTrackingPlacement(_clickoutType.value);

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

const offerLocationInjection = inject<OfferLocation>('offerLocation');
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const noOffer = inject<boolean | undefined>('noOffer', undefined);

const { isPromotionOffer, providerPromotion: providerPromo } = useProviderPromotion();

const isProviderPromo = computed(() => !buyboxOffer.value.bundleId && isPromotionOffer(buyboxOffer.value));

const jwRawEntityId = computed(() => episodeEntityId ?? props.title.id);

const { isArbitrating, withArbitrageTracking, trackTagEvent } = useArbitrage({ entityId: jwRawEntityId });

const shouldMaskClicks = computed(() => shouldClickMask(buyboxOffer.value?.package.packageId, country.value));

const rowNumber = computed(() => {
	if (offerContainer.value == null) return undefined;

	const parentBuyBox = offerContainer.value.closest?.('.buybox-selector') ?? null;
	const buyBoxRows = Array.from(parentBuyBox?.querySelectorAll('.buybox-selector > *') ?? []);
	const index = buyBoxRows.findIndex(row => row.contains(offerContainer.value));

	return index === -1 ? undefined : index;
});

const nativeAdRowIndex = computed(() => props.rowIndex ?? rowNumber.value);

const promoRibbonText = computed(() => providerPromo.value?.wideRibbon ?? providerPromo.value?.ribbon);

const disclaimerText = computed(() => {
	if (isProviderPromo.value && providerPromo.value?.offerText && providerPromo.value.offerDisclaimerText)
		return providerPromo.value.offerDisclaimerText;

	if (props.offer.package.shortName === 'adb' && isDvdOrBluRay.value) {
		return tc('WEBAPP_AMAZON_DVD_BLURAY_PRICE_DISCLAIMER');
	}

	return getDisclaimerText(props.offer.package.shortName, country.value);
});

const showDisclaimerButton = computed(() => !!disclaimerText.value);

const { rowCountry } = useInternationalOffers();
const { isActive: isInternationalOffersActive } = useInternationalOffersStatus({ title: () => props.title });

const { setPrimePlayPromoTags } = usePrimePlayPromoExp();

function clickoutBase(url: ClickoutUrl) {
	return url
		.addContexts(titleContext.value)
		.setForTracking('offerLocation', offerLocationInjection)
		.updateContext('ClickoutContextGraphql', 'rowNumber', rowNumber.value)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.updateContext(
			'ClickoutContextGraphql',
			'placement',
			isInternationalOffersActive.value ? `international_offers${props.isEpisode ? '_episode' : ''}` : placement
		)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
		.updateContext('ClickoutContextGraphql', 'noOffer', noOffer)
		.setTitleSourceId(titleSourceId)
		.set('uct_country', country.value)
		.set('uct_buybox', props.isPromotedBuyBox ? BuyboxParam.Promoted : BuyboxParam.Normal)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.setCanary(xServeTrack.value)
		.setGenre(props.title.content.genres)
		.setPageType(props.title.objectType);
}

const offerUrl = computed(() => {
	let clickoutUrl = new ClickoutUrl(buyboxOffer.value);

	if (isProviderPromo.value && providerPromo.value?.url) {
		const offer = { ...buyboxOffer.value, standardWebURL: providerPromo.value.url };
		clickoutUrl = new ClickoutUrl(offer).skipRedirect();
	}

	if (shouldMaskClicks.value) {
		const maskedUrl = clickMaskUrl(buyboxOffer.value.standardWebURL!);
		const offer = { ...buyboxOffer.value, standardWebURL: maskedUrl };
		clickoutUrl = new ClickoutUrl(offer).skipRedirect();
	}

	if (isArbitrating.value && ['amp', 'atp'].includes(buyboxOffer.value.package.shortName)) {
		const arbitrageUrl = withArbitrageTracking({ offer: buyboxOffer, title: () => props.title, placement: 'ro' });

		if (arbitrageUrl) {
			const url = arbitrageUrl.apply(clickoutBase);
			// EXP_PROVIDER_BUYBOX_V2
			url.apply(u =>
				setProviderBuyboxExpTag(
					u,
					'ro',
					buyboxOffer.value.package.shortName,
					buyboxOffer.value.monetizationType
				)
			);
			// EXP_PROVIDER_BUYBOX_V2
			// EXP_LINEAR_TV
			url.apply(u =>
				setLinearTVExpTags({
					url: u,
					type: 'ro',
					shortName: buyboxOffer.value.package.shortName,
					technicalName: buyboxOffer.value.package.technicalName,
				})
			);
			// EXP_LINEAR_TV
			return url;
		}
	}

	const url = clickoutUrl.apply(clickoutBase);

	if (rowCountry.value) {
		url.set('uct_country', rowCountry.value);
	}

	if (props.isBundle) {
		const packageContext = new SnowplowPackageContext(
			buyboxOffer.value.package.packageId,
			buyboxOffer.value.package.technicalName,
			'bundle',
			buyboxOffer.value.bundleId,
			buyboxOffer.value.bundleTechnicalName
		);
		clickoutUrl.addContexts(packageContext);

		// Bypass UCT for US users on bundle amazonstarzamazonhbomax
		if (buyboxOffer.value.bundleTechnicalName === 'amazonstarzamazonhbomax') {
			url.set('uct_ct', 'bypass');
		}
	}

	// EXP MERGE BUY RENT
	url.apply(url =>
		setBuyRentExpTags({
			url: url,
			type: 'ro',
			shortName: buyboxOffer.value.package.shortName,
			monetizationType: buyboxOffer.value.monetizationType,
		})
	);
	// EXP MERGE BUY RENT
	url.apply(u =>
		setSofterFreeTrialV2Tags(
			u,
			buyboxOffer.value.package.shortName,
			props.isPromotedBuyBox ? 'po' : 'ro',
			props.title.objectType,
			buyboxOffer.value.package.technicalName
		)
	);

	url.apply(url =>
		setPrimePlayPromoTags(url, 'ro', buyboxOffer.value.package.shortName, buyboxOffer.value.package.technicalName)
	);

	url.apply(url => setGridMoreInfoTags(url, 'ro', buyboxOffer.value.package.shortName));

	// EXP_PROMOTED_FREE_TRIAL_REDESIGN
	url.apply(u => setPromotedFreeTrialRedesignTags(u, 'ro'));
	// EXP_PROMOTED_FREE_TRIAL_REDESIGN

	// EXP_LINEAR_TV
	url.apply(url =>
		setLinearTVExpTags({
			url,
			type: 'ro',
			shortName: buyboxOffer.value.package.shortName,
			technicalName: buyboxOffer.value.package.technicalName,
		})
	);
	// EXP_LINEAR_TV

	// EXP_PROVIDER_BUYBOX_V2
	url.apply(url =>
		setProviderBuyboxExpTag(url, 'ro', buyboxOffer.value.package.shortName, buyboxOffer.value.monetizationType)
	);
	// EXP_PROVIDER_BUYBOX_V2

	// Amazon streaming bounty redirects
	if ([9, 119].includes(buyboxOffer.value.package.packageId)) {
		const redirect = url.get('r');
		return url.set('r', redirect!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	return url;
});

const isLeavingIconShown = computed(() => {
	if (isNativeAdCandidate.value) return false;
	return (
		!!buyboxOffer.value.availableTo &&
		diffInDays(new Date(buyboxOffer.value.availableTo)) < 30 &&
		diffInDays(new Date(buyboxOffer.value.availableTo)) >= 0
	);
});

const leavingLabel = computed(() => {
	if (!isLeavingIconShown.value) return '';
	const diffDays = diffInDays(new Date(buyboxOffer.value.availableTo));
	if (diffDays === 0) return tc('WEBAPP_LEAVING_TODAY');
	if (diffDays === 1) return tc('WEBAPP_LEAVING_TOMORROW');
	return t('WEBAPP_LEAVING_X_DAYS', { days: diffDays });
});

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

const selectedPackagePlan = computed(() => {
	// we have plan offers in 2 separate places because of international offers.
	// for gql it's not easily doable to include international offer's provider plan offers in the package.
	// that's why we take both places here into account.
	const planOffers = buyboxOffer.value?.plans.length
		? buyboxOffer.value?.plans
		: buyboxOffer.value?.package.planOffers;
	if (planOffers.length) {
		const plan = planOffers[0];
		// if a plan has a children that means it's a free trial plan and that always has priority.
		if (plan.children.length > 0) {
			return {
				...plan.children[0],
				retailPrice: plan.retailPrice,
				retailPriceConverted: plan.retailPriceConverted,
			};
		}
		return plan;
	}
	return null;
});

const freeTrialText = computed(() => {
	if (props.isBundle) {
		return null;
	}
	// Free trial data from API
	if (selectedPackagePlan.value?.isTrial) {
		if (selectedPackagePlan.value.durationDays === 1) return t('WEBAPP_1_DAY_FREE');
		return t('WEBAPP_X_DAYS_FREE', {
			days: selectedPackagePlan.value.durationDays,
		});
	}
	return null;
});

const hasFreeTrial = computed(() => {
	return !!freeTrialText.value && buyboxOffer.value?.monetizationType === MonetizationType.Flatrate;
});

// currently used by Sky DE only, this replaces the retail price with 'Sky Angebote' text
const hasOfferDetails = computed(() => buyboxOffer.value.package.shortName === 'skg' && country.value === 'DE');
const offerDetails = computed(() => {
	const provider = buyboxOffer.value.package.shortName === 'skg' ? 'Sky' : buyboxOffer.value.package.technicalName;

	return t('WEBAPP_PROVIDER_OFFER', { provider });
});

const retailPrice = computed(() => {
	if (props.isBundle) {
		return null;
	} else if (buyboxOffer.value?.retailPrice) {
		if (
			buyboxOffer.value.country !== country.value &&
			buyboxOffer.value.retailPriceConverted &&
			buyboxOffer.value.retailPrice != buyboxOffer.value.retailPriceConverted
		) {
			return `${buyboxOffer.value.retailPrice} (${buyboxOffer.value.retailPriceConverted})`;
		}
		return buyboxOffer.value.retailPrice;
	} else if (selectedPackagePlan.value && selectedPackagePlan.value.retailPrice) {
		// Retail price from API

		let retailPrice = selectedPackagePlan.value.retailPrice;
		if (
			buyboxOffer.value.country !== country.value &&
			selectedPackagePlan.value?.retailPriceConverted &&
			selectedPackagePlan.value.retailPrice != selectedPackagePlan.value.retailPriceConverted
		) {
			retailPrice = `${selectedPackagePlan.value.retailPrice} (${selectedPackagePlan.value.retailPriceConverted})`;
		}

		return t(
			buyboxOffer.value.monetizationType === MonetizationType.Flatrate && hasFreeTrial.value
				? 'WEBAPP_THEN_X_PER_MONTH'
				: 'WEBAPP_X_PER_MONTH',
			{ price: retailPrice }
		);
	}
	return null;
});

const isShowPageTVOD = computed(
	() =>
		[MonetizationType.Buy, MonetizationType.Rent].includes(buyboxOffer.value.monetizationType) &&
		props.title.objectType === ObjectType.Show &&
		!props.isEpisode
);

const minEpisodeTVODPriceOnShows = computed(() => {
	if (
		!isShowPageTVOD.value ||
		props.title.__typename !== 'Show' ||
		!buyboxOffer.value.minRetailPrice ||
		isDvdOrBluRay.value
	)
		return null;

	return buyboxOffer.value.minRetailPrice;
});

const minSeasonTVODPriceOnShows = computed(() => {
	const seasonIdsOnOffer = buyboxOffer.value.offerSeasons ?? [];
	if (
		props.title.__typename !== 'Show' ||
		!seasonIdsOnOffer.length ||
		![MonetizationType.Buy, MonetizationType.Rent].includes(buyboxOffer.value.monetizationType)
	) {
		return null;
	}
	// all seasonIds that are on the Show offer
	// we heuristically calculate the min price from the firstSeason, so we don't need to query all seasons and all its offers
	const filteredSeasons = props.title.firstSeason.filter(season => seasonIdsOnOffer.includes(season.id));
	if (!filteredSeasons.length) return null;

	// find the lowest price of all seasons
	const formattedMinPrice = filteredSeasons
		.map(season =>
			season.offers.find(
				offer =>
					offer.monetizationType === buyboxOffer.value.monetizationType &&
					buyboxOffer.value.package.shortName === offer.package.shortName &&
					buyboxOffer.value.presentationType === offer.presentationType
			)
		)
		.filter(offer => offer?.retailPrice != null && offer?.retailPriceValue != null && offer?.retailPriceValue !== 0)
		.sort((a, b) => a!.retailPriceValue! - b!.retailPriceValue!)[0]?.retailPrice;

	return formattedMinPrice;
});

const minSeasonTVODPriceOnShowsText = computed(() => {
	const seasonIdsOnOffer = buyboxOffer.value.offerSeasons ?? [];
	if (
		props.title.__typename !== 'Show' ||
		!seasonIdsOnOffer.length ||
		![MonetizationType.Buy, MonetizationType.Rent].includes(buyboxOffer.value.monetizationType)
	) {
		return null;
	}

	if (isDvdOrBluRay.value) {
		const seasonIdsOnOffer = buyboxOffer.value.offerSeasons ?? [];
		// we heuristically calculate the min price from the firstSeason, so we don't need to query all seasons and all its offers
		const filteredSeasons = props.title.firstSeason.filter(
			season =>
				seasonIdsOnOffer.includes(season.id) &&
				season.offers.find(
					offer =>
						offer.monetizationType === buyboxOffer.value.monetizationType &&
						buyboxOffer.value.package.shortName === offer.package.shortName &&
						buyboxOffer.value.presentationType === offer.presentationType
				)
		);
		if (buyboxOffer.value.type === OfferType.Standard) {
			if (buyboxOffer.value.country !== country.value && buyboxOffer.value.retailPriceConverted) {
				return `${buyboxOffer.value.retailPrice} (${buyboxOffer.value.retailPriceConverted})`;
			}
			return buyboxOffer.value.retailPrice;
		}
		if (filteredSeasons.length <= 1) {
			return minSeasonTVODPriceOnShows.value;
		}
		return t('WEBAPP_MIN_PRICE_PER_SEASON', {
			price: minSeasonTVODPriceOnShows.value,
		});
	} else {
		return t('WEBAPP_MIN_PRICE_PER_SEASON', {
			price: minSeasonTVODPriceOnShows.value,
		});
	}
});

const hasMinPriceTVOD = computed(() => !!minEpisodeTVODPriceOnShows.value || !!minSeasonTVODPriceOnShows.value);

const showRetailPrice = computed(
	() => !!retailPrice.value && buyboxOffer.value?.monetizationType !== MonetizationType.Free
);

const isSponsoredOffer = computed(() =>
	SponsoredClickoutsHelper.isSponsoredOfferGraphql(buyboxOffer.value?.package.packageId, webLocale.value)
		? 'nofollow sponsored noopener'
		: 'nofollow noopener'
);

const playerModalByProvider: Record<string, { cmp: () => Promise<any> }> = {
	plexplayer: {
		cmp: () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue'),
	},
	ottstudioplayer: {
		cmp: () => import('@/ui-components/video-player-modals/OttPlayerModal.vue'),
	},
	brollieplayer: {
		cmp: () => import('@/ui-components/video-player-modals/BrolliePlayerModal.vue'),
	},
};

const ESTTimeZone = 'America/New_York';
const PSTTimeZone = 'America/Los_Angeles';
const GermanyTimeZone = 'Europe/Berlin';

const linearOfferTimeZones = computed(() =>
	country.value === 'DE'
		? [{ key: 'CET', timeZone: GermanyTimeZone }]
		: [
				{ key: 'PST', timeZone: PSTTimeZone },
				{ key: 'EST', timeZone: ESTTimeZone },
		  ]
);

const formattedDate = computed(() => {
	if (!buyboxOffer.value?.availableFromTime) {
		return null;
	}

	const availableFromTime = new Date(buyboxOffer.value.availableFromTime);

	return {
		Day: formatDateTime(availableFromTime, language.value, country.value, {
			day: 'numeric',
			month: 'short',
			timeZone:
				country.value === 'US'
					? ESTTimeZone
					: isMounted.value
					? Intl.DateTimeFormat().resolvedOptions().timeZone
					: 'UTC',
		}),
		zones: linearOfferTimeZones.value.map(({ key, timeZone }) => ({
			key,
			label:
				formatDateTime(availableFromTime, language.value, country.value, {
					hour: '2-digit',
					minute: '2-digit',
					hour12: true,
					timeZone,
				}) + ` ${key}`,
		})),
	};
});

// EXP_LINEAR_TV
const { activeVariant: linearTVVariant } = useExperiment('EXP_LINEAR_TV', 'local');
const { trackEvent } = useExperiment('EXP_LINEAR_TV');
const isLinearTVVariant = computed(() => linearTVVariant.value);
const isLinearTVVariant1 = computed(() => linearTVVariant.value === 'variant_1');
const isLinearTVVariant2 = computed(() => linearTVVariant.value === 'variant_2');

const isLinearOffer = computed(
	() =>
		buyboxOffer.value.monetizationType === MonetizationType.LinearFlatrate ||
		buyboxOffer.value.monetizationType === MonetizationType.LinearFree ||
		buyboxOffer.value.monetizationType === MonetizationType.Fast
);

const linearAdditionalEpisodes = computed(() =>
	sortAndDedupeOfferEpisodes(buyboxOffer.value.offerEpisodes ?? []).slice(1)
);

const displayAdditionalEpisodes = computed(
	() =>
		linearAdditionalEpisodes.value &&
		linearAdditionalEpisodes.value.length > 0 &&
		filterOption.value &&
		[BuyBoxFilterOptions.FAST_TV, BuyBoxFilterOptions.LINEAR_TV].includes(filterOption.value)
);

const now = useNow({ interval: 60000 });

const episodeIsLive = computed(() => {
	if (!isMounted.value) return false;

	const { availableFromTime, availableToTime } = buyboxOffer.value;
	if (!availableFromTime || !availableToTime) return false;

	const from = new Date(availableFromTime).getTime();
	const to = new Date(availableToTime).getTime();
	if (Number.isNaN(from) || Number.isNaN(to)) return false;

	const nowMs = now.value.getTime();
	return from <= nowMs && nowMs < to;
});

function v2Date(availableFromTime: string | null | undefined) {
	if (!availableFromTime) {
		return null;
	}

	return formatDateTime(new Date(availableFromTime), language.value, country.value, {
		weekday: 'short',
		day: '2-digit',
		month: '2-digit',
	});
}

const DETimeZone = 'Europe/Berlin';

function v2Time(availableFromTime: string | null | undefined, availableToTime: string | null | undefined) {
	if (!availableFromTime) {
		return null;
	}

	const rawDateStart = new Date(availableFromTime as unknown as Date);
	const rawDateEnd = new Date(availableToTime as unknown as Date);

	const start = formatDateTime(rawDateStart, language.value, country.value, {
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23',
	});

	const end = formatDateTime(rawDateEnd, language.value, country.value, {
		hour: '2-digit',
		minute: '2-digit',
		timeZone:
			country.value === 'US'
				? ESTTimeZone
				: country.value === 'DE'
				? DETimeZone
				: isMounted.value
				? Intl.DateTimeFormat().resolvedOptions().timeZone
				: 'UTC',
		timeZoneName: 'short',
		hourCycle: 'h23',
	});

	return `${start} - ${end}`;
}
// EXP_LINEAR_TV

const { openRentalModal } = useRentalModal();
async function openModal() {
	if (!isModalProvider.value) return;

	if (isJWTVRentalOffer(buyboxOffer.value)) {
		// we trigger clickout first to get the final link context id as it's used to link events especially after page redirects.
		trackClickout(offerUrl.value);
		openRentalModal({
			offer: buyboxOffer.value,
			title: props.title,
			clickoutUrl: offerUrl.value,
			forceSafeOpen: isInternationalOffersActive.value,
		});
		return;
	}

	trackClickout(offerUrl.value);

	if (buyboxOffer.value.package.shortName === 'jwt') {
		await openJwTvAvodModal({
			modalProps: {
				offer: buyboxOffer.value,
				title: props.title,
				additionalContexts: getClickoutUrlContextsArray(offerUrl.value),
			},
		});
	} else {
		const modalData = playerModalByProvider[buyboxOffer.value.package.technicalName];
		ModalHelper.openModal(
			modalData.cmp,
			{
				offer: buyboxOffer.value,
				title: props.title,
				additionalContexts: getClickoutUrlContextsArray(offerUrl.value),
			},
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);
	}

	handleClickOut();
}

function toggleBuyBoxInfoModal(display: boolean) {
	displayBuyBoxInfoModal.value = display;

	if (displayBuyBoxInfoModal.value) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{ action: 'more_info_clicked', label: 'buybox' },
			props.additionalContexts
		);
	}
}

function handleClickOut() {
	trackTagEvent(() => buyboxOffer.value);

	// EXP_LINEAR_TV
	if (isLinearOffer.value) {
		const property = buyboxOffer.value.monetizationType.toLowerCase();
		trackEvent({ action: 'click', label: 'clickout', property });
	}
	// EXP_LINEAR_TV

	// EXP MERGE BUY RENT
	if (trackingEventLabel.value) {
		trackBuyRentExpEvent({
			action: 'click',
			label: trackingEventLabel.value,
			contexts: [offerUrl.value.contexts.ClickoutContextGraphql],
		});
	}
	// EXP MERGE BUY RENT
}

function onVisibilityChanged(isVisible: boolean) {
	onIntersection(offerUrl.value, isVisible);

	// EXP_LINEAR_TV
	if (isVisible && isLinearOffer.value) trackEvent({ action: 'impression' });
	// EXP_LINEAR_TV
}

function offerLabel(monetizationType: MonetizationType, presentationType: PresentationType) {
	if (isProviderPromo.value && providerPromo.value?.offerText) return providerPromo.value.offerText;
	if (props.isBundle) {
		return tc('WEBAPP_BUNDLE');
	}

	// EXP_LINEAR_TV
	// control keeps the old day-label fallback for linear-style offers
	if (isLinearOffer.value && !isLinearTVVariant.value && formattedDate.value?.['Day']) {
		return formattedDate.value?.['Day'];
	}

	if (
		presentationType === PresentationType.Dvd ||
		presentationType === PresentationType.Bluray ||
		presentationType === PresentationType.Bluray_4K
	) {
		if (monetizationType in DVD_BLURAY_MONETIZATION_TYPE_TRANSLATION_KEY[presentationType]) {
			return tc(DVD_BLURAY_MONETIZATION_TYPE_TRANSLATION_KEY[presentationType][monetizationType] as string);
		}
	}

	return tc(MONETIZATION_TYPE_TRANSLATION_KEY[monetizationType]);
}

return { __sfc: true,BuyBoxInfoModal, BundleIcons, ListOfferMBRExp, props, country, language, webLocale, t, tc, xServeTrack, isMounted, buyboxOffer, nativeAdsShortName, isNativeAdCandidate, setGridMoreInfoTags, setProviderBuyboxExpTag, setBuyRentExpTags, trackingEventLabel, setSofterFreeTrialV2Tags, setPromotedFreeTrialRedesignTags, setLinearTVExpTags, isDvdOrBluRay, expMergeBuyRentVariant, trackBuyRentExpEvent, isBundleWithIconWide, hardcodedPackage, displayBuyBoxInfoModal, offerContainer, isModalProvider, _clickoutType, clickoutType, placement, onIntersection, offerLocationInjection, titleContext, filterOption, titleSourceId, episodeEntityId, noOffer, isPromotionOffer, providerPromo, isProviderPromo, jwRawEntityId, isArbitrating, withArbitrageTracking, trackTagEvent, shouldMaskClicks, rowNumber, nativeAdRowIndex, promoRibbonText, disclaimerText, showDisclaimerButton, rowCountry, isInternationalOffersActive, setPrimePlayPromoTags, clickoutBase, offerUrl, isLeavingIconShown, leavingLabel, observeVisibilityOptions, selectedPackagePlan, freeTrialText, hasFreeTrial, hasOfferDetails, offerDetails, retailPrice, isShowPageTVOD, minEpisodeTVODPriceOnShows, minSeasonTVODPriceOnShows, minSeasonTVODPriceOnShowsText, hasMinPriceTVOD, showRetailPrice, isSponsoredOffer, playerModalByProvider, ESTTimeZone, PSTTimeZone, GermanyTimeZone, linearOfferTimeZones, formattedDate, linearTVVariant, trackEvent, isLinearTVVariant, isLinearTVVariant1, isLinearTVVariant2, isLinearOffer, linearAdditionalEpisodes, displayAdditionalEpisodes, now, episodeIsLive, v2Date, DETimeZone, v2Time, openRentalModal, openModal, toggleBuyBoxInfoModal, handleClickOut, onVisibilityChanged, offerLabel, ClickoutAnchor, ProviderIconWide, faClock, DisclaimerButton, ListOfferCTA, ListOfferPresentation, NativeAdListBuyboxOffer, LinearOfferCTA, LinearExtraShowings }
}

})